/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativos, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaPerfumes.module.scss";

// =====================================================
// DATOS IPSOS PERFUMES (Base: 400 casos / 365 usuarios)
// =====================================================
const IPSOS_PERFUMES = {
  penetracion: 0.906, // 90.6% usa perfumes habitualmente
  noPenetracion: 0.094, // 9.4% no usa
  marcas: {
    "Paco Rabane": 0.095,
    "Carolina Herrera": 0.083,
    "Victoria Secret": 0.073,
    Versace: 0.06,
    "Hugo Boss": 0.059,
    Lancome: 0.05,
    "Tomy Hilfiger": 0.046,
    "Jean Paul": 0.034,
    "La Polo": 0.03,
    212: 0.03,
  },
  prioridades: {
    Aroma: 0.737,
    Duración: 0.131,
    Precio: 0.082,
    Exclusividad: 0.029,
    Calidad: 0.012,
    "Siempre la misma colonia": 0.01,
  },
  frecuenciaCompra: {
    mensual: { pct: 0.061, factor: 12, label: "Mensual" },
    bimestral: { pct: 0.1, factor: 6, label: "Cada 2 meses" },
    trimestral: { pct: 0.18, factor: 4, label: "Cada 3 meses" },
    cuatrimestral: { pct: 0.086, factor: 3, label: "Cada 4 meses" },
    semestral: { pct: 0.174, factor: 2, label: "Cada 6 meses" },
    anual: { pct: 0.353, factor: 1, label: "1 vez al año" },
    cualquierFecha: { pct: 0.011, factor: 2, label: "Cualquier fecha" },
    noCompra: { pct: 0.036, factor: 0, label: "No compra" },
  },
  cantidadPerfumes: {
    1: 0.399,
    2: 0.184,
    3: 0.181,
    4: 0.06,
    5: 0.097,
    "6+": 0.079, // 2.7% + 5.2%
  },
};

// Opciones de ticket promedio (CLP)
const TICKETS_PERFUME = [50000, 75000, 100000, 125000];

// Escenarios de captación
const ESCENARIOS_CAPTACION = [2, 4, 6, 8, 10];

export const Perfumes = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("Paco Rabane");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);
  const [ticketPromedio, setTicketPromedio] = useState(75000);
  const [verAnual, setVerAnual] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativos({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // =====================================================
  // CÁLCULOS PRINCIPALES
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const usanPerfume = Math.round(totalEjecutivos * IPSOS_PERFUMES.penetracion);
    const noUsan = Math.round(totalEjecutivos * IPSOS_PERFUMES.noPenetracion);

    // Desglose por frecuencia de compra
    const desgloseFrecuencia = {};
    let comprasMensuales = 0;

    Object.entries(IPSOS_PERFUMES.frecuenciaCompra).forEach(([freq, data]) => {
      const personas = Math.round(usanPerfume * data.pct);
      const compras = Math.round(personas * data.factor);

      desgloseFrecuencia[freq] = {
        pct: data.pct,
        label: data.label,
        personas,
        compras,
      };

      comprasMensuales += compras;
    });

    const mercadoMensual = comprasMensuales * ticketPromedio;

    // Market share por marca (sorted by share descending)
    const marketShareData = Object.entries(IPSOS_PERFUMES.marcas)
      .sort(([, a], [, b]) => b - a)
      .map(([nombre, share]) => ({
        nombre,
        share,
        clientes: Math.round(usanPerfume * share),
        mercadoMensual: Math.round(mercadoMensual * share),
      }));

    // Desglose por prioridad
    const prioridades = {};
    Object.entries(IPSOS_PERFUMES.prioridades).forEach(([prioridad, pct]) => {
      prioridades[prioridad] = {
        personas: Math.round(usanPerfume * pct),
        pct: pct * 100,
      };
    });

    // Desglose por cantidad de perfumes
    const cantidadPerfumes = {};
    Object.entries(IPSOS_PERFUMES.cantidadPerfumes).forEach(([cantidad, pct]) => {
      cantidadPerfumes[cantidad] = {
        personas: Math.round(usanPerfume * pct),
        pct: pct * 100,
      };
    });

    return {
      usanPerfume,
      noUsan,
      desgloseFrecuencia,
      comprasMensuales,
      comprasAnuales: comprasMensuales * 12,
      mercadoMensual,
      mercadoAnual: mercadoMensual * 12,
      marketShareData,
      prioridades,
      cantidadPerfumes,
    };
  }, [totalEjecutivos, ticketPromedio]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN
  // =====================================================
  const calcularOportunidad = useCallback(
    (marca, pctCaptacion) => {
      if (!resultados) return null;

      const mercadoCaptadoMensual = Math.round(resultados.mercadoMensual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultados.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        pctDelTotal: pctCaptacion,
      };
    },
    [resultados]
  );

  if (loading || edificios.length === 0) return <Loader />;

  const frecuenciaKeys = Object.keys(IPSOS_PERFUMES.frecuenciaCompra);

  return (
    <div className={styles.perfumesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>PERFUMES</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.perfumesContent}>
        {/* Tabla de frecuencia de compra */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thNoCompran)}>No usan</th>
                  <th className={cx("text-center", styles.thSiCompran)}>Sí usan</th>
                  <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                  <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                  <th className={cx("text-center", styles.thConversiones)}>Conversiones {verAnual ? "año" : "mes"}</th>
                </tr>
              </thead>
              <tbody>
                {frecuenciaKeys.map((freq, idx) => {
                  const data = resultados.desgloseFrecuencia[freq];
                  return (
                    <tr key={freq}>
                      {idx === 0 && (
                        <>
                          <td
                            rowSpan={frecuenciaKeys.length}
                            className={cx("text-center align-middle", styles.celdaNo)}
                          >
                            <div className={styles.celdaContenido}>
                              <span className={cx(styles.badgeIpsos, styles.badgeOportunidad)}>
                                {(IPSOS_PERFUMES.noPenetracion * 100).toFixed(0)}%
                              </span>
                              <div className={styles.valorMuyGrande}>{formatNumber(resultados.noUsan)}</div>
                              <div className={styles.subtextoOportunidad}>OPORTUNIDAD</div>
                            </div>
                          </td>
                          <td
                            rowSpan={frecuenciaKeys.length}
                            className={cx("text-center align-middle", styles.celdaSi)}
                          >
                            <div className={styles.celdaContenido}>
                              <span className={styles.badgeIpsos}>
                                {(IPSOS_PERFUMES.penetracion * 100).toFixed(0)}%
                              </span>
                              <div className={styles.valorMuyGrande}>{formatNumber(resultados.usanPerfume)}</div>
                            </div>
                          </td>
                        </>
                      )}
                      <td className={styles.celdaFrecuencia}>
                        <div className={styles.frecuenciaInner}>
                          <span className={styles.badgeIpsos}>{(data.pct * 100).toFixed(0)}%</span>
                          <span>{data.label}</span>
                        </div>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">
                        {freq === "noCompra" ? "-" : formatNumber(verAnual ? data.compras * 12 : data.compras)}
                      </td>
                    </tr>
                  );
                })}
                <tr className={styles.filaTotales}>
                  <td colSpan={4} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.comprasAnuales : resultados.comprasMensuales)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Ticket + Mercado + Toggle */}
        {resultados && (
          <div className={styles.dosBloquesContainer}>
            {/* Ticket promedio */}
            <div className={styles.bloqueTicket}>
              <div className={styles.ticketLabel}>Ticket promedio</div>
              <div className={styles.ticketBox}>
                <div className={styles.ticketOpciones}>
                  {TICKETS_PERFUME.map((t) => (
                    <label key={t} className={styles.ticketRadio}>
                      <input
                        type="radio"
                        name="ticketPromedio"
                        checked={ticketPromedio === t}
                        onChange={() => setTicketPromedio(t)}
                      />
                      <span>{formatLocal(convertCLP(t))}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Mercado */}
            <div className={styles.bloqueMercado}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
              </div>
            </div>
          </div>
        )}

        {/* Market Share por Marca */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share por Marca</h4>
            <div className={styles.marketShareGrid}>
              {resultados.marketShareData.map((data) => (
                <div key={data.nombre} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{data.nombre}</div>
                  <div className={styles.cardShare}>
                    <span className={styles.badgeIpsos}>{(data.share * 100).toFixed(0)}%</span>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Clientes</span>
                      <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                    </div>
                    <div>
                      <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Tablas de desglose: Prioridades + Cantidad perfumes */}
        {resultados && (
          <div className={styles.tablasDesglose}>
            <div className={styles.desgloseContainer}>
              <h4>¿Qué priorizan al comprar?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Prioridad</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.prioridades).map(([prioridad, data]) => (
                    <tr key={prioridad}>
                      <td className="text-start">{prioridad}</td>
                      <td className="text-center">
                        <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.desgloseContainer}>
              <h4>¿Cuántos perfumes usa?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">Cantidad</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.cantidadPerfumes).map(([cantidad, data]) => (
                    <tr key={cantidad}>
                      <td className="text-center">{cantidad}</td>
                      <td className="text-center">
                        <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos / 365 usuarios de perfumes. El 73.7% prioriza
            aroma, 13.1% duración. Top marcas: Paco Rabane (9.5%), Carolina Herrera (8.3%), Victoria Secret (7.3%).
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(
                    ({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork, created_at }) => (
                      <tr
                        key={created_at}
                        onClick={() => toggleCheck(id)}
                        className={cx(checkedBuildings[id] && "table-success")}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="text-center">
                          {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                        </td>
                        <td className="text-center">{edificio.toUpperCase()}</td>
                        <td className="text-center">{ubicacion}</td>
                        <td className="text-center">
                          {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                        </td>
                        <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Perfumes</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>Marca objetivo:</label>
                    <select
                      value={marcaSeleccionada}
                      onChange={(e) => setMarcaSeleccionada(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {Object.keys(IPSOS_PERFUMES.marcas).map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado:</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION.map((p) => (
                        <option key={p} value={p}>
                          {p}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad(marcaSeleccionada, porcentajeCaptacion);
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Inversión referencia (3 meses):</span>
                        <span className={styles.resultadoValor}>{formatLocal(convertCLP(6000000))}</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Perfumes;
